import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { BusinessConfig } from '../../types';
import { getCurrentBusinessConfig, getCurrentEmployee, getEmployeeWorkHours, updateBusinessConfig } from '../../firebase/firebase-delegate';
import {
  DAYS_OF_WEEK_PREFERRED_ORDER,
} from '../../constants';
import {
  convertHoursToEntries,
  entriesToHoursMap,
  BusinessHoursEntry,
  DayKey,
  cloneBusinessHoursEntries,
  convertWorkHoursToBusinessEntries,
} from '../../utils/businessHours';
import BusinessHoursCard from '../../components/admin/BusinessHoursCard';
import HomepageBusinessHoursCard from '../../components/homepage/BusinessHoursCard';
import CustomDialogWithContent from '../../components/global/CustomDialogWithContent';
import { Edit } from '@mui/icons-material';

const createEmptyHourErrors = (): Record<DayKey, string> =>
  DAYS_OF_WEEK_PREFERRED_ORDER.reduce(
    (acc, day) => ({
      ...acc,
      [day]: '',
    }),
    {} as Record<DayKey, string>
  );

const WebsiteDetailsPage: React.FC = () => {
  const businessConfigQuery = useQuery<BusinessConfig | null>({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });

  const [aboutUs, setAboutUs] = useState('');
  const [aboutDraft, setAboutDraft] = useState('');
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const [businessHoursEntries, setBusinessHoursEntries] = useState<Record<DayKey, BusinessHoursEntry>>(
    () => convertHoursToEntries()
  );
  const [initialHoursSnapshot, setInitialHoursSnapshot] =
    useState<BusinessConfig['businessInfo']['businessHours'] | null>(null);
  const [hourErrors, setHourErrors] = useState<Record<DayKey, string>>(() => createEmptyHourErrors());
  const [isHoursDialogOpen, setIsHoursDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const employeeQuery = useQuery({
    queryKey: ['currentEmployee'],
    queryFn: getCurrentEmployee,
  });

  const workHoursQuery = useQuery({
    queryKey: ['currentEmployeeWorkHours', employeeQuery.data?.id],
    queryFn: () => getEmployeeWorkHours(employeeQuery.data!.id),
    enabled: !!employeeQuery.data?.id,
  });

  const workHoursEntries = useMemo(
    () => (workHoursQuery.data ? convertWorkHoursToBusinessEntries(workHoursQuery.data) : null),
    [workHoursQuery.data]
  );

  useEffect(() => {
    if (!businessConfigQuery.data) return;
    setAboutUs(businessConfigQuery.data.homepage?.aboutUs ?? '');
    setAboutDraft(businessConfigQuery.data.homepage?.aboutUs ?? '');
    setIsAboutExpanded(false);
    const entries = convertHoursToEntries(businessConfigQuery.data.businessInfo?.businessHours);
    setBusinessHoursEntries(entries);
    setInitialHoursSnapshot(entriesToHoursMap(entries));
    setHourErrors(createEmptyHourErrors());
  }, [businessConfigQuery.data]);

  const handleDayEntryChange = (day: DayKey, nextEntry: BusinessHoursEntry) => {
    setBusinessHoursEntries((prev) => ({
      ...prev,
      [day]: nextEntry,
    }));
    setHourErrors((prev) => ({ ...prev, [day]: '' }));
  };

  const handleMatchWorkHours = () => {
    if (!workHoursEntries) return;
    setBusinessHoursEntries(cloneBusinessHoursEntries(workHoursEntries));
    setHourErrors(createEmptyHourErrors());
  };

  const updateMutation = useMutation({
    mutationFn: async (payload: BusinessConfig) => updateBusinessConfig(payload),
    onSuccess: () => {
      businessConfigQuery.refetch();
      setSnackbar({ open: true, message: 'Changes saved successfully.', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message || 'Failed to update website details.', severity: 'error' });
    },
  });

  const sanitizeHoursForSave = () => entriesToHoursMap(businessHoursEntries);

  const validateHours = () => {
    let isValid = true;
    const nextErrors: Record<DayKey, string> = createEmptyHourErrors();
    DAYS_OF_WEEK_PREFERRED_ORDER.forEach((day) => {
      const entry = businessHoursEntries[day];
      if (entry.isClosed) {
        nextErrors[day] = '';
        return;
      }
      if (!entry.startTime || !entry.endTime) {
        nextErrors[day] = 'Select both start and end times or mark the day Closed.';
        isValid = false;
        return;
      }
      if (!entry.endTime.isAfter(entry.startTime)) {
        nextErrors[day] = 'End time must be after start time.';
        isValid = false;
        return;
      }
      nextErrors[day] = '';
    });
    setHourErrors(nextErrors);
    return isValid;
  };

  const handleOpenAboutDialog = () => {
    setAboutDraft(aboutUs);
    setIsAboutDialogOpen(true);
  };

  const handleCloseAboutDialog = () => {
    setAboutDraft(aboutUs);
    setIsAboutDialogOpen(false);
  };

  const handleSaveAbout = () => {
    if (!businessConfigQuery.data) return;
    const trimmed = aboutDraft.trim();
    setAboutUs(trimmed);
    setIsAboutDialogOpen(false);
    const payload: BusinessConfig = {
      ...businessConfigQuery.data,
      homepage: {
        ...businessConfigQuery.data.homepage,
        aboutUs: trimmed,
      },
    };
    updateMutation.mutate(payload);
  };

  const handleOpenHoursDialog = () => {
    setIsHoursDialogOpen(true);
  };

  const handleCloseHoursDialog = () => {
    if (!initialHoursSnapshot) {
      setIsHoursDialogOpen(false);
      return;
    }
    setBusinessHoursEntries(convertHoursToEntries(initialHoursSnapshot));
    setHourErrors(createEmptyHourErrors());
    setIsHoursDialogOpen(false);
  };

  const handleSaveHours = () => {
    if (!businessConfigQuery.data) return;
    if (!validateHours()) {
      setSnackbar({ open: true, message: 'Please fix the highlighted business hours.', severity: 'error' });
      return;
    }
    const sanitizedHours = sanitizeHoursForSave();
    const payload: BusinessConfig = {
      ...businessConfigQuery.data,
      businessInfo: {
        ...businessConfigQuery.data.businessInfo,
        businessHours: sanitizedHours,
      },
    };
    setIsHoursDialogOpen(false);
    updateMutation.mutate(payload);
  };

  if (businessConfigQuery.isLoading) {
    return <LoadingSpinner />;
  }

  if (businessConfigQuery.isError || !businessConfigQuery.data) {
    return (
      <Container sx={{ mt: 6 }}>
        <Typography variant="h6" color="error" align="center">
          Unable to load website details. Please try again later.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
        These changes would be reflected on your public website.
      </Typography>
      <Box sx={{ mb: 4, maxWidth: 560, mx: 'auto' }}>
        <Box
          sx={{
            bgcolor: 'grey.100',
            px: 2,
            py: 1,
            borderRadius: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            About Us
          </Typography>
          <IconButton size="small" onClick={handleOpenAboutDialog} aria-label="Edit about us">
            <Edit fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Typography variant="body1" color={aboutUs ? 'text.primary' : 'text.secondary'}>
          {aboutUs ? (
            isAboutExpanded || aboutUs.length <= 250 ? (
              aboutUs
            ) : (
              <>
                {aboutUs.slice(0, 250)}...{' '}
                <Box
                  component="span"
                  onClick={() => setIsAboutExpanded(true)}
                  sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600 }}
                >
                  See More
                </Box>
              </>
            )
          ) : (
            'No info added yet'
          )}
        </Typography>
        {aboutUs && aboutUs.length > 250 && isAboutExpanded && (
          <Box
            component="button"
            onClick={() => setIsAboutExpanded(false)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              marginTop: 8,
              cursor: 'pointer',
              color: 'var(--mui-palette-primary-main)',
              fontWeight: 600,
            }}
          >
            See Less
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 2, maxWidth: 560, mx: 'auto' }}>
        <Box
          sx={{
            bgcolor: 'grey.100',
            px: 2,
            py: 1,
            borderRadius: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Business Hours
          </Typography>
          <IconButton size="small" onClick={handleOpenHoursDialog} aria-label="Edit business hours">
            <Edit fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ my: 1 }} />
        <HomepageBusinessHoursCard
          hours={businessConfigQuery.data.businessInfo.businessHours}
          hideHeader
        />
      </Box>

      <CustomDialogWithContent
        open={isAboutDialogOpen}
        title="Edit About Us"
        fullScreenOnMobile
        stickyActions
        actionButtonVariant="contained"
        actionButtonColor="inherit"
        actionButtonSx={{ bgcolor: 'common.black', color: 'common.white', '&:hover': { bgcolor: 'grey.900' } }}
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: { xs: '100%', sm: 520 } }}>
            <Box
              component="textarea"
              value={aboutDraft}
              onChange={(e) => setAboutDraft(e.target.value)}
              style={{
                width: '100%',
                minHeight: 160,
                resize: 'vertical',
                padding: '12px',
                borderRadius: 8,
                border: '1px solid #000',
                fontFamily: 'inherit',
                fontSize: '1rem',
              }}
            />
          </Box>
        }
        actionButtonText={updateMutation.isPending ? 'Saving...' : 'Save'}
        onAction={handleSaveAbout}
        onCancel={handleCloseAboutDialog}
      />

      <CustomDialogWithContent
        open={isHoursDialogOpen}
        title="Edit Business Hours"
        fullScreenOnMobile
        stickyActions
        actionButtonVariant="contained"
        actionButtonColor="inherit"
        actionButtonSx={{ bgcolor: 'common.black', color: 'common.white', '&:hover': { bgcolor: 'grey.900' } }}
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: { xs: '100%', sm: 560 } }}>
            <Box display="flex" alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Button
                variant="outlined"
                size="small"
                disabled={!workHoursEntries || workHoursQuery.isLoading}
                onClick={handleMatchWorkHours}
                sx={{
                  textTransform: 'none',
                  borderColor: 'common.black',
                  color: 'common.black',
                  '&:hover': {
                    borderColor: 'common.black',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                Match Work Hours
              </Button>
            </Box>
            {!workHoursEntries && !workHoursQuery.isLoading && (
              <Typography variant="caption" color="text.secondary">
                Set up your work hours first to enable matching.
              </Typography>
            )}
            <Box>
              {DAYS_OF_WEEK_PREFERRED_ORDER.map((day) => (
                <BusinessHoursCard
                  key={day}
                  day={day}
                  entry={businessHoursEntries[day]}
                  onChange={(nextEntry) => handleDayEntryChange(day, nextEntry)}
                  error={hourErrors[day]}
                />
              ))}
            </Box>
          </Box>
        }
        actionButtonText={updateMutation.isPending ? 'Saving...' : 'Save'}
        onAction={handleSaveHours}
        onCancel={handleCloseHoursDialog}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WebsiteDetailsPage;
